import { Button } from '@strata/react-buttons';
import { WarningDialog } from '@strata/react-dialog';
import { type ColumnDef, Grid } from '@strata/react-grid';
import { Hyperlink } from '@strata/react-hyperlink';
import { ErrorToast, SuccessToast } from '@strata/react-toast-message';
import { useState } from 'react';

import {
    type AutomationRuleToDelete,
    postDeletePrescriberList,
    postDeleteStateList,
} from 'api/auctionsAPI';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';

const ListType = { PRESCRIBER: 'Prescriber', STATE: 'State' } as const;

interface DeleteListDialogProps {
    listType: (typeof ListType)[keyof typeof ListType];
    listToDelete?: {
        prescriberListId?: string;
        stateListId?: string;
        listName: string;
    };
    onDeleteSuccess: () => void;
    onClose: () => void;
}

const onDeleteFunc: Record<
    (typeof ListType)[keyof typeof ListType],
    (
        npi: string,
        list: Required<DeleteListDialogProps>['listToDelete'],
    ) => Promise<AutomationRuleToDelete[]>
> = {
    [ListType.PRESCRIBER]: (npi, list) =>
        postDeletePrescriberList({ npi, prescriberListId: list.prescriberListId! }),
    [ListType.STATE]: (npi, list) => postDeleteStateList({ npi, stateListId: list.stateListId! }),
};

const columns: ColumnDef<AutomationRuleToDelete>[] = [
    {
        accessorKey: 'description',
        header: 'Automation Rules',
        cell: ({ getValue, row }) => (
            <Hyperlink
                linkText={getValue()}
                navUrl={AppRoutes.automationDetails(row.original.bid_id)}
            />
        ),
    },
];

export const DeleteListDialog = ({
    listType,
    listToDelete = undefined,
    onDeleteSuccess,
    onClose,
}: DeleteListDialogProps) => {
    const selectedNpi = useAppSelector((state) => state.auth.selectedNpi);
    const [automationRulesToDelete, setAutomationRulesToDelete] =
        useState<AutomationRuleToDelete[]>();
    const hasRulesToDelete = (automationRulesToDelete ?? []).length > 0;
    return (
        <WarningDialog
            dialogTitle={
                !hasRulesToDelete
                    ? `Confirm Delete ${listType} List`
                    : `Delete ${listType} List Warning`
            }
            width={hasRulesToDelete ? 600 : undefined}
            isOpen={!!listToDelete}
            primaryButtonLabel={!hasRulesToDelete ? `Yes, Delete ${listType} List` : 'Retry Delete'}
            primaryButtonIntent={!hasRulesToDelete ? Button.Intent.DANGER : Button.Intent.PRIMARY}
            onPrimaryButtonClick={() => {
                void (async () => {
                    try {
                        if (selectedNpi && listToDelete) {
                            const result = await onDeleteFunc[listType](selectedNpi, listToDelete);

                            if (result?.length > 0) {
                                ErrorToast({
                                    message: `This ${listType?.toLowerCase()} list cannot be deleted because it is used in automation rules. See dialog below for details.`,
                                });
                                setAutomationRulesToDelete(result);
                            } else {
                                onDeleteSuccess();
                                SuccessToast({ message: `${listType} list successfully deleted.` });
                                onClose();
                                setAutomationRulesToDelete(undefined);
                            }
                        }
                    } catch (err) {
                        handleError(
                            `Error deleting ${listType?.toLowerCase()} list.`,
                            err as Error,
                        );
                    }
                })();
            }}
            secondaryButtonLabel={!hasRulesToDelete ? `No, Return To ${listType} Lists` : 'Cancel'}
            onSecondaryButtonClick={() => {
                onClose();
                setAutomationRulesToDelete(undefined);
            }}
            dialogContent={
                <>
                    <div className="margin__bottom--large">
                        {!hasRulesToDelete
                            ? `Are you sure you want to delete the following ${listType?.toLowerCase()} list?`
                            : `This ${listType?.toLowerCase()} list cannot be deleted because it is used in automation rules. When you have removed this ${listType?.toLowerCase()} list from the following automation rules you can click Retry Delete.`}
                    </div>
                    <div className="margin__bottom--large font-semi-bold">
                        {listToDelete?.listName}
                    </div>
                    {hasRulesToDelete && (
                        <div className="margin__bottom--large">
                            <Grid data={automationRulesToDelete} columns={columns} />
                        </div>
                    )}
                </>
            }
        />
    );
};

DeleteListDialog.ListType = ListType;
